// CollectionTile.jsx - Individual game tile component for Collection
import React from "react";
import { Pencil, Trash2 } from "lucide-react";
import { HEART_ICON, THUMBS_UP_ICON, NEUTRAL_ICON, THUMBS_DOWN_ICON } from "../../constants/icons";
import OnlineBadge from "./OnlineBadge";
import PlayListButton from "../common/PlayListButton";

/**
 * Check if a game is in "interested" status
 */
export const isInterestedGame = (game) => {
  const status = (game.ownership_status || "").toLowerCase();
  return status === "interested" && !game.isShared;
};

/**
 * Check if a game has a valid rating
 */
export const hasValidRating = (game) => {
  const preferenceValue = game.preference || game.rating;
  return (
    preferenceValue !== null &&
    preferenceValue !== undefined &&
    preferenceValue !== "" &&
    preferenceValue !== 0
  );
};

/**
 * Get the rating display value
 */
export const getRatingValue = (game) => {
  return game.preference || game.rating;
};

/**
 * Individual collection game tile component
 */
function CollectionTile({
  game,
  lastPlayed,
  onClick,
  onRemove,
  onEdit,
  onRatingClick,
  onlineVenues,
  user,
  onTogglePlayList,
  inPlayList,
}) {
  const gameId = game.game_id || game.id;
  const interested = isInterestedGame(game);
  const rating = getRatingValue(game);
  const showRating = hasValidRating(game);
  const ownershipStatus = (game.ownership_status || "").toLowerCase();
  const isInterestedStatus = ownershipStatus === "interested";
  const isAnonymous = !user || !user.id;

  return (
    <div className="collection-tile" data-game-id={gameId} data-game-name={game.name}>
      <div
        style={{
          position: "relative",
          pointerEvents: "auto",
          cursor: "pointer",
        }}
        onClick={() => onClick(game)}
      >
        <img
          src={game.thumbnail || "/placeholder-game.png"}
          alt={game.name}
          className="collection-tile-image"
        />

        {/* Online Badge - Top Center */}
        <OnlineBadge onlineVenues={onlineVenues} />

        {/* Edit Button for All Games */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(game);
          }}
          style={{
            position: "absolute",
            top: "8px",
            left: "8px",
            backgroundColor: interested ? "rgba(46, 125, 50, 0.9)" : "rgba(33, 150, 243, 0.9)",
            color: "white",
            border: "none",
            borderRadius: "4px",
            padding: "4px 8px",
            fontSize: "0.75rem",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
          title={interested ? "Add Rating" : "Edit Game"}
        >
          <Pencil size={14} />
        </button>

        {/* Preference Banner */}
        {showRating && (
          <div
            data-testid="preference-banner"
            onClick={(e) => {
              e.stopPropagation();
              onRatingClick(game);
            }}
            style={{
              position: "absolute",
              top: "8px",
              left: isInterestedStatus ? "auto" : "8px",
              right: isInterestedStatus ? "8px" : "auto",
              backgroundColor: "var(--pista-green-primary, #2E7D32)",
              color: "#fff",
              border: "none",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "0.75rem",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {rating === 'like_a_lot' && HEART_ICON}
            {rating === 'like' && THUMBS_UP_ICON}
            {rating === 'neutral' && NEUTRAL_ICON}
            {rating === 'dislike' && THUMBS_DOWN_ICON}
          </div>
        )}

        {/* Last Played Banner */}
        {lastPlayed?.last_played && (
          <div
            className="last-played-badge"
            style={{
              position: "absolute",
              bottom: "8px",
              left: "8px",
              backgroundColor: "rgba(46, 125, 50, 0.9)",
              color: "white",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "0.75rem",
              fontWeight: "bold",
            }}
          >
            {(() => {
              const days = Math.floor((Date.now() - new Date(lastPlayed.last_played).getTime()) / (1000 * 60 * 60 * 24));
              if (days === 0) return "Today";
              if (days === 1) return "1 day ago";
              if (days < 30) return `${days} days ago`;
              const months = Math.floor(days / 30);
              if (months === 1) return "1 month ago";
              return `${months} months ago`;
            })()}
          </div>
        )}

        {/* Tags Display - horizontally spanning banner with 50% opacity */}
        {game.additional_tags && game.additional_tags.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "0",
              right: "0",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(46, 125, 50, 0.5)",
              color: "white",
              padding: "6px 8px",
              fontSize: "0.9rem",
              fontWeight: "bold",
              textTransform: "uppercase",
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              pointerEvents: "none",
            }}
          >
            {game.additional_tags.slice(0, 2).join(" • ")}
            {game.additional_tags.length > 2 &&
              ` • +${game.additional_tags.length - 2}`}
          </div>
        )}

        {/* Play List Button - Top Right Corner */}
        {onTogglePlayList && (
          <div
            style={{
              position: "absolute",
              top: "8px",
              right: "38px",
            }}
          >
            <PlayListButton
              isActive={inPlayList}
              isAnonymous={isAnonymous}
              onClick={() => onTogglePlayList(game)}
              dataTestId="collection-tile-playlist-button"
            />
          </div>
        )}

        {/* Remove Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(gameId);
          }}
          className="remove-button"
          aria-label={`Remove ${game.name} from collection`}
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            backgroundColor: "rgba(220, 53, 69, 0.9)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "bold",
          }}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Game Info */}
      <div style={{ padding: "0.5rem" }}>
        <h4
          style={{
            margin: 0,
            fontSize: "0.875rem",
            fontWeight: "600",
            color: "var(--text-primary, #333)",
            lineHeight: "1.2",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {game.name}
        </h4>
        {game.year_published && (
          <p
            style={{
              margin: "0.25rem 0 0 0",
              fontSize: "0.75rem",
              color: "var(--text-secondary, #666)",
            }}
          >
            {game.year_published}
          </p>
        )}
      </div>
    </div>
  );
}

export default CollectionTile;
